import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import colors from '../../../styling/colors';
import countryCodes from '../../../countryCodes';
import { openLogInModal } from '../../../redux/modals';
import { login } from '../../../redux/account';
import HandOffLink from '../../../utils/urls/HandOffLink';
import { getCookie } from '../../../utils/cookie';
import { ErrorMessage, ErrorButton } from '../SiteTakeovers/Alerts';
import { AccountContainer } from './ModalContainers/ModalContainers';
import ButtonWithUnderline from '../Buttons/ButtonWithUnderline';
import SocialButtons from './AuthModalComponents/SocialButtons';
// TODO: MOVE THIS TO COMMON/Forms
import {
  StyledForm,
  Label,
  CheckboxInput,
  Submit,
  DisplayCheckbox,
} from './AuthModalComponents/Form';
// TODO: MOVE THIS TO COMMON/Forms
import {
  AuthEmailInput,
  AuthPasswordInput,
  Main,
  Container,
  Section,
  StyledDivider,
} from './AuthModalComponents/Layout';

const StyledDisplayCheckbox = styled(DisplayCheckbox)`
  border: 1px solid ${colors('lapel')};
`;

const NoticeText = styled.div`
  color: ${colors('silver')};
  font-size: 0.75rem;
  line-height: 0.75rem;
  width: 100%;
`;

const MarketingNoticeText = styled(NoticeText)`
  color: ${colors('lapel')};
`;

const NoticeLink = styled(HandOffLink)`
  color: ${colors('silver')};
  font-weight: bold;
`;

const Marketing = styled.div`
  display: flex;
  margin-bottom: 1rem;
  text-align: left;
  width: 100%;
`;

export const SignupModal = ({
  accountLoading,
  errorType,
  errorMessage,
  isMobile,
  openLogInModal,
  redirectUrl,
  login,
  userAgent,
}) => {
  const [allowMarketing, setAllowMarketing] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const handleLogin = e => {
    e.preventDefault();
    openLogInModal();
  };

  const handleSubmit = e => {
    e.preventDefault();

    // Attempt a login first, the failure to successfully login will result in a sign up.
    login({
      email,
      password,
      additionalSignUpInfo: { allowMarketing, userAgent },
    });
  };

  // on initial mount check the checkbox if the user is in the US
  useEffect(() => {
    if (getCookie('country_code') === countryCodes.US) {
      setAllowMarketing(true);
    }
  }, []);

  return (
    <AccountContainer isMobile={isMobile}>
      <Main>
        <Container>
          <Section>
            <SocialButtons redirectUrl={redirectUrl} type="signup">
              Sign up
            </SocialButtons>
            <NoticeText>
              We wonʼt post anything without your permission.
            </NoticeText>
            <StyledDivider>sign up with email</StyledDivider>
            <StyledForm onSubmit={handleSubmit}>
              {errorMessage.length ? (
                <ErrorMessage>
                  {errorMessage.indexOf(
                    'This account has already been created.',
                  ) === -1 ? (
                    errorMessage
                  ) : (
                    <div>
                      This account has already been created.
                      <ErrorButton onClick={handleLogin}>
                        Already have an account?
                      </ErrorButton>
                    </div>
                  )}
                </ErrorMessage>
              ) : null}
              <AuthEmailInput
                error={
                  errorMessage.length &&
                  (errorType === 'email' || errorType.length === 0)
                }
                onChange={event => setEmail(event.target.value)}
                placeholder="Enter your email"
                type="email"
                value={email}
              />
              <AuthPasswordInput
                error={
                  !!(
                    errorMessage.length &&
                    (errorType === 'password' || errorType.length === 0)
                  )
                }
                onChange={event => setPassword(event.target.value)}
                placeholder="Create a password"
                type="password"
                value={password}
              />
              <Marketing>
                <Label>
                  <CheckboxInput
                    onChange={event => setAllowMarketing(event.target.checked)}
                    type="checkbox"
                  />
                  <StyledDisplayCheckbox checked={allowMarketing} />
                  <MarketingNoticeText>
                    Be the first to hear about discounts and new designs. You
                    can unsubscribe from emails at any time.
                  </MarketingNoticeText>
                </Label>
              </Marketing>
              <Submit disabled={accountLoading} type="submit">
                Sign Up
              </Submit>
            </StyledForm>
            <NoticeText>
              By signing up, via Google, Facebook or email, I agree to Paperless
              Post’s&nbsp;
              <NoticeLink
                href="/info/terms/terms_of_service/paperless_post_terms_of_service"
                identifier="signup-terms-of-service"
                target="_blank"
              >
                Terms of Service
              </NoticeLink>
              &nbsp;and&nbsp;
              <NoticeLink
                href="/info/terms/privacy/privacy_policy"
                identifier="signup-notice-link"
                target="_blank"
              >
                Privacy Policy
              </NoticeLink>
              .
            </NoticeText>
            <ButtonWithUnderline onClick={handleLogin}>
              Have an account? <span>Log in.</span>
            </ButtonWithUnderline>
          </Section>
        </Container>
      </Main>
    </AccountContainer>
  );
};

SignupModal.propTypes = {
  accountLoading: PropTypes.bool,
  errorMessage: PropTypes.string,
  openLogInModal: PropTypes.func.isRequired,
  redirectUrl: PropTypes.string,
  login: PropTypes.func.isRequired,
  userAgent: PropTypes.string.isRequired,
};

SignupModal.defaultProps = {
  accountLoading: false,
};

const mapStateToProps = ({
  account: { loading, errorMessage, errorType },
  env: { userAgent, isMobile },
  modals: { redirectUrl },
}) => ({
  isMobile,
  userAgent,
  accountLoading: loading,
  errorMessage,
  errorType,
  redirectUrl,
});

const mapDispatchToProps = {
  login,
  openLogInModal,
};

export default connect(mapStateToProps, mapDispatchToProps)(SignupModal);
